/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, Button } from "@mui/material";
import { useState } from "react";
import loadImage from 'blueimp-load-image'

type ImgProps = {
  data: string
  date?: string
  latitude?: number
  longitude?: number
}

type Props = {
  name: string
  value: ImgProps
  children: string
  onChange: (e: any) => void
}

// 1件のドキュメントに画像アップロード項目が2〜3個含まれるため、
// Firestoreのドキュメント上限（1MiB）に収まるよう1枚あたりの上限を抑える
const MAX_FILE_SIZE = 300 * 1024 // 300KB（保存後のdata URL文字列のバイト数）

// Firestoreに保存されるのは data URL 文字列そのもの（base64込み）なので、
// その文字列のバイト数（≒文字数、ASCII文字のみのため）を実サイズとして算出する
const getStoredByteSize = (dataUrl: string) => dataUrl.length

// 上限を超える場合は画質・サイズを段階的に落として再エンコードする
const shrinkToMaxSize = (canvas: HTMLCanvasElement, maxBytes: number) => {
  let quality = 0.85
  let width = canvas.width
  let height = canvas.height
  let workingCanvas = canvas
  let dataUrl = workingCanvas.toDataURL('image/jpeg', quality)

  while (getStoredByteSize(dataUrl) > maxBytes && quality > 0.1) {
    quality -= 0.1
    dataUrl = workingCanvas.toDataURL('image/jpeg', quality)
  }

  while (getStoredByteSize(dataUrl) > maxBytes && width > 200 && height > 200) {
    width = Math.round(width * 0.8)
    height = Math.round(height * 0.8)
    const scaledCanvas = document.createElement('canvas')
    scaledCanvas.width = width
    scaledCanvas.height = height
    scaledCanvas.getContext('2d')?.drawImage(workingCanvas, 0, 0, width, height)
    workingCanvas = scaledCanvas
    dataUrl = workingCanvas.toDataURL('image/jpeg', quality)
  }

  return dataUrl
}

export const UploadImage = (props: Props) => {
  const { value, onChange} = props
  const [fileData, setFileData] = useState<ImgProps>(value)

  const onChangeFile = async ({ target }: any) => {
    const { files, name } = target
    if(files.length == 0) return
    const [file] = files
    await loadImage(file, { meta: true, canvas: true, maxWidth: 1000, maxHeight: 1000, orientation: true })
      .then((data) => {
        const fileData = {
          data: shrinkToMaxSize(data.image as HTMLCanvasElement, MAX_FILE_SIZE)
        }
        const Exif = data.exif as any
        if (Exif) {
          const date = Exif.get("DateTime")
          const GpsInfo = Exif.get('GPSInfo') as any
          const gpsLatitude = GpsInfo?.get('GPSLatitude')
          const gpsLatitudeRef = GpsInfo?.get('GPSLatitudeRef')
          const gpsLongitude = GpsInfo?.get('GPSLongitude')
          const gpsLongitudeRef = GpsInfo?.get('GPSLongitudeRef')
          const latitude = toPercentage(gpsLatitudeRef, gpsLatitude)
          const longitude = toPercentage(gpsLongitudeRef, gpsLongitude)
          fileData["date"] = date
          fileData["latitude"] = latitude
          fileData["longitude"] = longitude
        }
        setFileData(fileData)
        onChange({ value: fileData, name })
      })
  }

  const toPercentage = (ref: string, geo: number) => {
    if (ref === 'N' || ref === 'E') {
      return geo[0] + geo[1] / 60 + geo[2] / 3600;
    } else if (ref === 'S' || ref === 'W') {
      return -(geo[0] + geo[1] / 60 + geo[2] / 3600);
    }
  }

  return (
    <Box display="contents" justifyContent="center">
      <label htmlFor={`upload-button-${props.name}`}>
        <input
          accept="image/*"
          id={`upload-button-${props.name}`}
          name={props.name}
          multiple
          type="file"
          onChange={onChangeFile}
          style={{ display: "none" }}
        />
        <Button variant="contained" component="span">
          {props.children}
        </Button>
      </label>
      <Box mt={2}>
        <img src={fileData && fileData.data} width={300}/>
      </Box>
    </Box>

  );
};
